<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api, type Document, type UploadResponse } from '../lib/api';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import { uploadSessionStore } from '../lib/stores/uploadSessionStore';
  import {
    FileText,
    AlertTriangle,
    ArrowRight,
    Upload,
    ScanText,
    Tag,
    BarChart3,
  } from 'lucide-svelte';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Card, Button } from '../lib/components/ui/index';
  import DocumentUpload from '../lib/components/DocumentUpload.svelte';
  import DocumentTable from '../lib/components/DocumentTable.svelte';

  import type { PaginatedResponse } from '../lib/api';
  import type { FilterValue } from '../lib/components/ui/FilterToolbar.svelte';

  let recentDocuments: Document[] = [];
  let loading = true;
  let error: string | null = null;
  let paginationData: PaginatedResponse<Document>['pagination'] | null = null;

  // Sort, filter & pagination state
  let sortBy: string | null = 'uploadedAt';
  let sortOrder: 'asc' | 'desc' | null = 'desc';
  let pageSize = 10;
  let page = 0;
  let cursorStack: (string | undefined)[] = [undefined];
  let fileFormat: string | null = null;
  let searchQuery: string | null = null;
  let fromDate: string | null = null;
  let toDate: string | null = null;
  let minSize: number | null = null;
  let maxSize: number | null = null;
  let selectedCategory: string | null = null;
  let selectedSubCategory: string | null = null;
  let selectedAssignedTo: string | null = null;

  $: filterState = {
    search: searchQuery,
    category: selectedCategory,
    subCategory: selectedSubCategory,
    fileFormat,
    fromDate,
    toDate,
    minSize,
    maxSize,
    assignedTo: selectedAssignedTo,
  };

  function resetCursorStack() {
    cursorStack = [undefined];
    page = 0;
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    sortBy = e.detail.order ? e.detail.key : null;
    sortOrder = e.detail.order;
    resetCursorStack();
    loadRecentDocuments();
  }

  function handlePageChange(e: CustomEvent<number>) {
    page = e.detail;
    loadRecentDocuments();
  }

  function handlePageSizeChange(e: CustomEvent<number>) {
    pageSize = e.detail;
    resetCursorStack();
    loadRecentDocuments();
  }

  function handleClearAll() {
    searchQuery = null;
    fileFormat = null;
    fromDate = null;
    toDate = null;
    minSize = null;
    maxSize = null;
    selectedCategory = null;
    selectedSubCategory = null;
    selectedAssignedTo = null;
    resetCursorStack();
    loadRecentDocuments();
  }

  function handleFilter(e: CustomEvent<{ key: string; value: FilterValue }>) {
    const { key, value } = e.detail;

    if (key === 'search') {
      searchQuery = typeof value === 'string' ? value : null;
    } else if (key === 'fileFormat') {
      fileFormat = Array.isArray(value) && value.length > 0 ? value[0] : null;
    } else if (key === 'fileSize') {
      const v = value as { min?: number | null; max?: number | null } | null;
      minSize = v?.min ?? null;
      maxSize = v?.max ?? null;
    } else if (key === 'dateRange') {
      const v = value as { from?: string | null; to?: string | null } | null;
      fromDate = v?.from ?? null;
      toDate = v?.to ?? null;
    } else if (key === 'category') {
      selectedCategory = Array.isArray(value) && value.length > 0 ? value[0] : null;
      selectedSubCategory = null;
    } else if (key === 'subCategory') {
      selectedSubCategory = Array.isArray(value) && value.length > 0 ? value[0] : null;
    } else if (key === 'assignedTo') {
      selectedAssignedTo = Array.isArray(value) && value.length > 0 ? value[0] : null;
    }

    resetCursorStack();
    loadRecentDocuments();
  }

  async function loadRecentDocuments() {
    loading = true;
    error = null;

    try {
      const params: Record<string, string | number | null | undefined> = {
        pageSize,
        sortBy: sortBy || 'uploadedAt',
        sortOrder: sortOrder || 'desc',
        cursor: cursorStack[page],
        householdId: $activeHouseholdStore,
      };

      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSubCategory) params.subCategory = selectedSubCategory;
      if (fileFormat) params.fileFormat = fileFormat;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (minSize != null) params.minSize = minSize;
      if (maxSize != null) params.maxSize = maxSize;
      if (selectedAssignedTo) params.assignedTo = selectedAssignedTo;

      const docsResponse = await api.documents.listDocuments(
        params as Parameters<typeof api.documents.listDocuments>[0],
      );

      recentDocuments = docsResponse.items;
      paginationData = docsResponse.pagination;

      // Store next page cursor if we haven't seen it yet
      const nextPage = page + 1;
      if (docsResponse.pagination.nextCursor && cursorStack.length <= nextPage) {
        cursorStack = [...cursorStack, docsResponse.pagination.nextCursor];
      }
    } catch (err) {
      error = err instanceof Error ? err.message : $_('errors.generic');
      if (err instanceof Error && err.message.includes('401')) {
        navigate('/login');
      }
    } finally {
      loading = false;
    }
  }

  function handleUploaded(event: CustomEvent<UploadResponse>) {
    loadRecentDocuments();
    uploadSessionStore.startSession(event.detail.uploadBatchId, event.detail.documents);
    navigate(`/upload/${event.detail.uploadBatchId}`);
  }

  // Reload when active household changes
  let prevHouseholdId = $activeHouseholdStore;
  $: if ($activeHouseholdStore !== prevHouseholdId) {
    prevHouseholdId = $activeHouseholdStore;
    resetCursorStack();
    loadRecentDocuments();
  }

  onMount(() => {
    loadRecentDocuments();
  });
</script>

<PageHeader title={$_('nav.dashboard')} />

{#if recentDocuments.length === 0 && !loading && !searchQuery && !selectedCategory && !fromDate && !toDate}
  <div class="py-12 px-6 bg-surface border border-border rounded-xl text-center mb-8">
    <div class="max-w-2xl mx-auto">
      <div
        class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6 text-accent"
      >
        <FileText class="w-8 h-8" strokeWidth={1.5} />
      </div>
      <h2 class="text-2xl font-semibold text-text mb-2">{$_('onboarding.welcome_title')}</h2>
      <p class="text-text-secondary mb-10">{$_('onboarding.welcome_subtitle')}</p>

      <!-- Guided Steps -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 text-left mb-10">
        <div class="p-4 bg-bg-alt/50 rounded-lg border border-border/50 relative">
          <div
            class="absolute -top-3 -left-3 w-7 h-7 bg-surface border border-border text-accent rounded-full flex items-center justify-center text-xs font-semibold"
          >
            1
          </div>
          <h3 class="font-semibold text-sm text-text mb-1">{$_('onboarding.step_1_title')}</h3>
          <p class="text-xs text-text-muted leading-relaxed">{$_('onboarding.step_1_desc')}</p>
        </div>
        <div class="p-4 bg-bg-alt/50 rounded-lg border border-border/50 relative">
          <div
            class="absolute -top-3 -left-3 w-7 h-7 bg-surface border border-border text-accent rounded-full flex items-center justify-center text-xs font-semibold"
          >
            2
          </div>
          <h3 class="font-semibold text-sm text-text mb-1">{$_('onboarding.step_2_title')}</h3>
          <p class="text-xs text-text-muted leading-relaxed">{$_('onboarding.step_2_desc')}</p>
        </div>
        <div class="p-4 bg-bg-alt/50 rounded-lg border border-border/50 relative">
          <div
            class="absolute -top-3 -left-3 w-7 h-7 bg-surface border border-border text-accent rounded-full flex items-center justify-center text-xs font-semibold"
          >
            3
          </div>
          <h3 class="font-semibold text-sm text-text mb-1">{$_('onboarding.step_3_title')}</h3>
          <p class="text-xs text-text-muted leading-relaxed">{$_('onboarding.step_3_desc')}</p>
        </div>
      </div>

      <!-- KI-Pipeline Explanation -->
      <div class="mb-10">
        <p class="text-xs text-text-muted uppercase tracking-wide mb-4">
          {$_('onboarding.pipeline_title')}
        </p>
        <div class="flex items-center justify-center gap-2 flex-wrap">
          <div
            class="flex items-center gap-1.5 px-3 py-1.5 bg-bg-alt rounded-lg border border-border/50"
          >
            <Upload class="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
            <span class="text-xs text-text">{$_('onboarding.pipeline_step_1')}</span>
          </div>
          <ArrowRight class="w-3.5 h-3.5 text-text-muted hidden sm:block" strokeWidth={1.5} />
          <div
            class="flex items-center gap-1.5 px-3 py-1.5 bg-bg-alt rounded-lg border border-border/50"
          >
            <Tag class="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
            <span class="text-xs text-text">{$_('onboarding.pipeline_step_2')}</span>
          </div>
          <ArrowRight class="w-3.5 h-3.5 text-text-muted hidden sm:block" strokeWidth={1.5} />
          <div
            class="flex items-center gap-1.5 px-3 py-1.5 bg-bg-alt rounded-lg border border-border/50"
          >
            <ScanText class="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
            <span class="text-xs text-text">{$_('onboarding.pipeline_step_3')}</span>
          </div>
          <ArrowRight class="w-3.5 h-3.5 text-text-muted hidden sm:block" strokeWidth={1.5} />
          <div
            class="flex items-center gap-1.5 px-3 py-1.5 bg-bg-alt rounded-lg border border-border/50"
          >
            <BarChart3 class="w-3.5 h-3.5 text-accent" strokeWidth={1.5} />
            <span class="text-xs text-text">{$_('onboarding.pipeline_step_4')}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-col items-center gap-3">
        <Button variant="primary" size="lg" on:click={() => navigate('/settings')}>
          {$_('onboarding.get_started')}
          <ArrowRight class="w-4 h-4 ml-1" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  </div>
{/if}

<!-- Upload Zone -->
<div class="mb-6">
  <DocumentUpload on:uploaded={handleUploaded} />
</div>

<!-- Recent Documents -->
<Card padding="none">
  <div class="flex items-center justify-between px-4 py-3 border-b border-border">
    <h2 class="text-base font-semibold text-text">{$_('pages.home.recent_uploads')}</h2>
    <Button variant="ghost" size="sm" on:click={() => navigate('/browse')}>
      {$_('pages.home.view_all')}
      <ArrowRight class="w-4 h-4" strokeWidth={1.5} />
    </Button>
  </div>

  {#if error}
    <div class="text-center py-6 px-4">
      <AlertTriangle class="w-8 h-8 text-error mx-auto mb-2" strokeWidth={1.5} />
      <p class="text-sm text-error mb-3">{error}</p>
      <Button variant="primary" size="sm" on:click={loadRecentDocuments}>
        {$_('pages.home.retry')}
      </Button>
    </div>
  {:else}
    <DocumentTable
      documents={recentDocuments}
      {loading}
      {sortBy}
      {sortOrder}
      {filterState}
      {page}
      {pageSize}
      totalCount={null}
      hasMore={paginationData?.hasMore ?? false}
      hasPrev={page > 0}
      on:sort={handleSort}
      on:filter={handleFilter}
      on:clearAll={handleClearAll}
      on:pageChange={handlePageChange}
      on:pageSizeChange={handlePageSizeChange}
    />
  {/if}
</Card>
